import { useState, useMemo, useCallback } from 'react';

/**
 * Reusable hook for inventory filtering and searching
 * Can be used in both TryOn Studio modal and Inventory page
 */
export const useInventoryFilters = (products = [], defaultGender = 'female') => {
  // Search query for name and SKU
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states - gender defaults to 'female' (Women)
  const [filters, setFilters] = useState({
    gender: defaultGender,
    color: '',
    category: '',
    stockStatus: 'all', // 'all', 'in_stock', 'out_of_stock'
    priceRange: { min: '', max: '' },
    sizes: []
  });

  // Extract unique filter options from products
  const filterOptions = useMemo(() => {
    const colors = [...new Set(products.map(p => p.color).filter(Boolean))];
    const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
    const genders = [...new Set(products.map(p => p.gender).filter(Boolean))];
    const sizes = [...new Set(
      products.flatMap(p => {
        if (p.variants) {
          return p.variants.map(v => v.size).filter(Boolean);
        }
        return p.size ? [p.size] : [];
      })
    )];
    
    // Calculate price range
    const prices = products
      .map(p => p.selling_price || p.mrp)
      .filter(p => p !== null && p !== undefined && !isNaN(p));
    
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

    return {
      colors,
      categories,
      genders,
      sizes,
      priceRange: { min: minPrice, max: maxPrice }
    };
  }, [products]);

  // Apply filters to products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search filter (name or SKU)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = (product.product_name || product.name || '').toLowerCase();
        const sku = (product.sku_id || '').toLowerCase();
        if (!name.includes(query) && !sku.includes(query)) {
          return false;
        }
      }

      // Gender filter
      if (filters.gender) {
        const productGender = (product.gender || '').toLowerCase();
        const filterGender = filters.gender.toLowerCase();
        // Show product if it matches the filter or is unisex
        if (productGender !== filterGender && productGender !== 'unisex') {
          return false;
        }
      }

      // Color filter
      if (filters.color && product.color !== filters.color) {
        return false;
      }
      
      // Category filter
      if (filters.category && product.category !== filters.category) {
        return false;
      }
      
      // Stock status filter
      const stock = product.total_stock || 0;
      if (filters.stockStatus === 'in_stock' && stock <= 0) {
        return false;
      }
      if (filters.stockStatus === 'out_of_stock' && stock > 0) {
        return false;
      }
      
      // Price range filter
      const price = product.selling_price || product.mrp;
      if (price !== null && price !== undefined) {
        if (filters.priceRange.min !== '' && price < parseFloat(filters.priceRange.min)) {
          return false;
        }
        if (filters.priceRange.max !== '' && price > parseFloat(filters.priceRange.max)) {
          return false;
        }
      } else {
        // If product has no price and price filter is set, exclude it
        if (filters.priceRange.min !== '' || filters.priceRange.max !== '') {
          return false;
        }
      }

      // Size filter
      if (filters.sizes.length > 0) {
        const productSizes = product.variants 
          ? product.variants.map(v => v.size).filter(Boolean)
          : (product.size ? [product.size] : []);
        
        if (!filters.sizes.some(s => productSizes.includes(s))) {
          return false;
        }
      }
      
      return true;
    });
  }, [products, searchQuery, filters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.gender) count++;
    if (filters.color) count++;
    if (filters.category) count++;
    if (filters.stockStatus !== 'all') count++;
    if (filters.priceRange.min !== '' || filters.priceRange.max !== '') count++;
    if (filters.sizes.length > 0) count++;
    return count;
  }, [filters]);

  // Update a single filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update price range
  const updatePriceRange = useCallback((minOrMax, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: { ...prev.priceRange, [minOrMax]: value }
    }));
  }, []);

  // Toggle size in sizes array
  const toggleSize = useCallback((size) => {
    setFilters(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      gender: defaultGender,
      color: '',
      category: '',
      stockStatus: 'all',
      priceRange: { min: '', max: '' },
      sizes: []
    });
  }, [defaultGender]);

  // Reset filters (without clearing search)
  const resetFilters = useCallback(() => {
    setFilters({
      gender: defaultGender,
      color: '',
      category: '',
      stockStatus: 'all',
      priceRange: { min: '', max: '' },
      sizes: []
    });
  }, [defaultGender]);

  return {
    // Search
    searchQuery,
    setSearchQuery,
    
    // Filters
    filters,
    setFilters,
    updateFilter,
    updatePriceRange,
    toggleSize,
    clearFilters,
    resetFilters,
    
    // Computed values
    filterOptions,
    filteredProducts,
    activeFilterCount,
    
    // Stats
    totalCount: products.length,
    filteredCount: filteredProducts.length
  };
};

export default useInventoryFilters;
